import type {
    MultiAssetValuationDataPoint,
    NormalizedMultiAssetValuationDataPoint,
    NormalizedMultiAssetValuationHistory,
} from '@einwert/valuation-api-client'
import { filterDataPointsWithUndefinedValue, monetaryToDecimal } from 'ComponentLibrary'
import type { LineChartYearFilteredData, LineData, MonetaryValueDataObjectType } from 'ComponentLibrary'

export type DashboardHistoryMode = 'absolute' | 'normalized'
export type DashboardHistorySeries = {
    data: LineData[]
    yearFilteredData?: LineChartYearFilteredData
}

type MonetaryHistoryMetric = keyof Pick<MultiAssetValuationDataPoint, 'market_value' | 'rental_income' | 'actual_rental_income'>

type DashboardHistorySeriesConfig = {
    id: string
    metric: MonetaryHistoryMetric
    color: string
    showTrends?: boolean
}

export type DashboardHistorySeriesByMode = Record<DashboardHistoryMode, DashboardHistorySeries>

type DashboardHistoryDataPoint = {
    year: number
    value?: number
    tooltipMonetaryValue?: MonetaryValueDataObjectType
    numberOfAssets?: number
    trendPeriodLabel?: string
}

function buildHistoryDataPoints(dataPoints?: DashboardHistoryDataPoint[]) {
    return filterDataPointsWithUndefinedValue(
        dataPoints?.map(({ year, ...dataPoint }) => ({
            date: String(year),
            ...dataPoint,
        }))
    )
        .sort((a, b) => a.date.localeCompare(b.date))
        .map((dataPoint, index, filteredDataPoints) => {
            const previousDataPoint = filteredDataPoints[index - 1]

            return {
                ...dataPoint,
                ...(previousDataPoint ? { trendPeriodLabel: `${previousDataPoint.date}-${dataPoint.date}` } : {}),
            }
        })
}

function getFirstNonZeroYear(dataPoints?: DashboardHistoryDataPoint[]) {
    return dataPoints?.toSorted((a, b) => a.year - b.year).find(({ value }) => value !== 0)?.year
}

function trimDataPointsBeforeYear(dataPoints: DashboardHistoryDataPoint[] | undefined, firstNonZeroYear: number | undefined) {
    return firstNonZeroYear === undefined ? [] : dataPoints?.filter(({ year }) => year >= firstNonZeroYear)
}

export function shouldShowNormalizedDashboardHistory(normalizedHistory?: NormalizedMultiAssetValuationHistory) {
    const marketValueDataPoints = normalizedHistory?.max ?? []

    if (marketValueDataPoints.length < 2) {
        return false
    }

    let unchangedDevelopmentCount = 0

    for (let index = 1; index < marketValueDataPoints.length; index += 1) {
        const currentDataPoint = marketValueDataPoints[index]
        const previousDataPoint = marketValueDataPoints[index - 1]

        if (currentDataPoint.market_value === previousDataPoint.market_value) {
            unchangedDevelopmentCount += 1
        }
    }

    const developmentCount = marketValueDataPoints.length - 1

    return unchangedDevelopmentCount / developmentCount < 0.45
}

export function buildDashboardHistorySeries({
    history,
    normalizedHistory,
    series,
    trimLeadingZeroes: shouldTrimLeadingZeroes,
}: {
    history?: MultiAssetValuationDataPoint[]
    normalizedHistory?: NormalizedMultiAssetValuationHistory
    series: DashboardHistorySeriesConfig[]
    trimLeadingZeroes?: boolean
}): DashboardHistorySeriesByMode {
    const historyByYear = new Map(history?.map((dataPoint) => [dataPoint.year, dataPoint]) || [])
    const buildAbsoluteDataPoints = (metric: MonetaryHistoryMetric) =>
        history?.map((dataPoint) => ({
            year: dataPoint.year,
            value: monetaryToDecimal(dataPoint[metric]),
            numberOfAssets: dataPoint.number_of_assets,
        }))
    const maybeTrimBeforeAbsoluteStart = (dataPoints: DashboardHistoryDataPoint[] | undefined, metric: MonetaryHistoryMetric) => {
        if (!shouldTrimLeadingZeroes) {
            return dataPoints
        }

        const absoluteDataPoints = buildAbsoluteDataPoints(metric)
        return trimDataPointsBeforeYear(dataPoints, getFirstNonZeroYear(absoluteDataPoints ?? dataPoints))
    }

    const buildNormalizedSeriesData = (dataPoints?: NormalizedMultiAssetValuationDataPoint[]) =>
        series.map(({ metric, ...seriesConfig }) => ({
            ...seriesConfig,
            data: buildHistoryDataPoints(
                maybeTrimBeforeAbsoluteStart(
                    dataPoints?.map((dataPoint) => ({
                        year: dataPoint.year,
                        value: dataPoint[metric],
                        tooltipMonetaryValue: historyByYear.get(dataPoint.year)?.[metric],
                        numberOfAssets: dataPoint.number_of_assets,
                    })),
                    metric
                )
            ),
        }))

    return {
        absolute: {
            data: series.map(({ metric, ...seriesConfig }) => ({
                ...seriesConfig,
                data: buildHistoryDataPoints(buildAbsoluteDataPoints(metric)),
            })),
        },
        normalized: {
            data: buildNormalizedSeriesData(normalizedHistory?.max),
            yearFilteredData: {
                3: buildNormalizedSeriesData(normalizedHistory?.last_3),
                5: buildNormalizedSeriesData(normalizedHistory?.last_5),
                10: buildNormalizedSeriesData(normalizedHistory?.last_10),
                15: buildNormalizedSeriesData(normalizedHistory?.last_15),
                [-1]: buildNormalizedSeriesData(normalizedHistory?.max),
            },
        },
    }
}
